<template>
  <div class="w-full flex flex-col">
    <HeroSection :loading="loading" @search="search" />
    <ProblemSection />
    <HowItWorksSection />
    <BenefitsSection />
    <PortalPreviewSection />
    <FaqSection />
    <AboutSection />
    <ClosingCtaSection :loading="loading" @search="search" />

    <PodcastSearchResults
      v-model="resultsVisible"
      :results="results"
      @choose="choose"
      @manual="chooseManually"
    />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AboutSection from '@/components/landing/AboutSection.vue'
import BenefitsSection from '@/components/landing/BenefitsSection.vue'
import ClosingCtaSection from '@/components/landing/ClosingCtaSection.vue'
import FaqSection from '@/components/landing/FaqSection.vue'
import HeroSection from '@/components/landing/HeroSection.vue'
import HowItWorksSection from '@/components/landing/HowItWorksSection.vue'
import PortalPreviewSection from '@/components/landing/PortalPreviewSection.vue'
import ProblemSection from '@/components/landing/ProblemSection.vue'
import PodcastSearchResults from '@/components/podcasts/PodcastSearchResults.vue'
import PodcastService from '@/services/PodcastService'
import PodcastSearchResult from '@/models/PodcastSearchResult'
import { useOnboardingStore } from '@/stores/OnboardingStore'
import { useUserStore } from '@/stores/UserStore'
import { useNotify } from '@/composables/Notify'
import { EventName, track } from '@/utils/analytics'

const term = ref('')
const results = ref<PodcastSearchResult[]>([])
const resultsVisible = ref(false)
const loading = ref(false)
const router = useRouter()
const { notifyError } = useNotify()
const onboardingStore = useOnboardingStore()
const userStore = useUserStore()

// Persisted stores are empty during SSR, so the logged-in check has to wait for
// hydration — branching in the template would mismatch.
onMounted(() => {
  if (userStore.isLoggedIn) router.replace({ name: 'dashboard' })
})

const search = async (value: string) => {
  // The search itself is the first thing a publisher does on the landing page,
  // and the only signal for people who look and leave. The term is not sent —
  // the count is what the funnel needs.
  track(EventName.podcastSearch)
  term.value = value
  loading.value = true
  try {
    results.value = await PodcastService.search(value)
    resultsVisible.value = true
  } catch (error) {
    notifyError(error)
  } finally {
    loading.value = false
  }
}

const choose = (result: PodcastSearchResult) => {
  onboardingStore.startWith(result)
  router.push({ name: 'start' })
}

const chooseManually = () => {
  onboardingStore.startWithName(term.value)
  router.push({ name: 'start' })
}
</script>
